import { Box, Link as ChakraLink } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { icon } from '@fortawesome/fontawesome-svg-core';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { RSecondTitle } from '@resistapp/client/components/headers/r-second-title';
import { RThirdTitle } from '@resistapp/client/components/headers/r-third-title';
import { Grid, GridCell } from '@resistapp/client/components/shared/grid';
import { FlexView } from '@resistapp/client/components/shared/layout';
import { oldTheme } from '@resistapp/client/components/shared/old-styles';
import { theme } from '@resistapp/client/components/shared/theme';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import {
  References as ReferencesList,
  RefSmall,
  SmallExternalLinkIcon,
} from '@resistapp/client/hooks/use-science-reference';
import { hasResearchAccessForProject, isExplorerOrDemo } from '@resistapp/common/features';
import {
  AnalysisDescriptionBlock,
  AnalysisDescriptionContent,
  FullProject,
  ReferenceLink,
} from '@resistapp/common/types';
import { ReactNode } from 'react';
import { DownloadAndLinkSection } from './download-and-link-section';

interface Props {
  project: FullProject;
  displayMode?: 'full' | 'ct-only';
}

export function MethodAndDownloadSections(props: Props) {
  const { project, displayMode = 'full' } = props;
  const { user } = useUser();

  // Return null components if the user is not logged in or is part of a demo organisation
  if (isExplorerOrDemo(user)) {
    return null;
  }

  const hasResearchAccess = hasResearchAccessForProject(user, project.id);
  const isCtOnly = displayMode === 'ct-only' || !hasResearchAccess;
  const hasAnalysisDescription = project.analysisDescription != null;

  if (isCtOnly) {
    return (
      <Grid>
        {hasAnalysisDescription && (
          <GridCell widthpercentage={66}>
            <AnalysisDescription analysisDescription={project.analysisDescription} />
          </GridCell>
        )}
        <GridCell widthpercentage={hasAnalysisDescription ? 34 : 100}>
          <DownloadAndLinkSection project={project} displayMode="ct-only" />
        </GridCell>
      </Grid>
    );
  }

  return (
    <Grid>
      <GridCell widthpercentage={66}>
        <AnalysisDescription analysisDescription={project.analysisDescription} />
      </GridCell>
      <GridCell widthpercentage={34}>
        <DownloadAndLinkSection project={project} />
      </GridCell>
    </Grid>
  );
}

export function AnalysisDescription(props: { analysisDescription?: AnalysisDescriptionContent }) {
  const { analysisDescription } = props;
  const { user } = useUser();
  if (!analysisDescription) {
    return null;
  }
  const showReferences = !isExplorerOrDemo(user);
  const dataProcessing = analysisDescription.dataProcessing ?? [];
  const hasDataProcessing = dataProcessing.length > 0;
  const hasReferences = showReferences && analysisDescription.references.length > 0;
  return (
    <FlexView>
      <RSecondTitle>SmartChip qPCR Analysis Description</RSecondTitle>
      {renderBlocks(analysisDescription.intro, showReferences, analysisDescription.references)}
      {hasDataProcessing && (
        <>
          <RThirdTitle>Data Processing and Analysis</RThirdTitle>
          {renderBlocks(dataProcessing, showReferences, analysisDescription.references)}
        </>
      )}
      {hasReferences && (
        <Box>
          <RThirdTitle>References</RThirdTitle>
          <ReferencesList>
            {analysisDescription.references.map((ref, idx) => (
              <RefSmall key={ref.href} href={ref.href} target="_blank" rel="noreferrer">
                <Superscript $hasLeadingSpace={false}>{idx + 1}</Superscript> {ref.label}{' '}
                <SmallExternalLinkIcon icon={icon(faExternalLinkAlt)} />
              </RefSmall>
            ))}
          </ReferencesList>
        </Box>
      )}
    </FlexView>
  );
}

function renderBlocks(
  blocks: AnalysisDescriptionBlock[],
  includeReferences: boolean,
  references: ReferenceLink[],
): ReactNode {
  return blocks.map((block, idx) => {
    if (block.type === 'list') {
      return (
        <BulletList key={`list-${idx}`}>
          {block.items.map((item, itemIdx) => (
            <BulletItem key={`list-${idx}-${itemIdx}`}>{item}</BulletItem>
          ))}
        </BulletList>
      );
    }

    return (
      <Paragraph key={`paragraph-${idx}`}>
        {renderTextWithRefs(block.text, includeReferences, references)}
        {block.ctaHref && (
          <>
            {' '}
            <ChakraLink
              href={block.ctaHref}
              target={block.ctaHref.startsWith('http') ? '_blank' : undefined}
              rel={block.ctaHref.startsWith('http') ? 'noreferrer' : undefined}
            >
              {block.ctaLabel ?? block.ctaHref}
            </ChakraLink>
          </>
        )}
      </Paragraph>
    );
  });
}

function renderTextWithRefs(text: string, includeReferences: boolean, references: ReferenceLink[]): ReactNode {
  const nodes: ReactNode[] = [];
  const regex = /\{\{(ref|sup):([^}]+)\}\}/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const tokenType = match[1];

    if (tokenType === 'sup') {
      nodes.push(
        <InlineSuperscript key={`sup-${match.index}`}>
          {match[2]}
        </InlineSuperscript>,
      );
    } else if (tokenType === 'ref') {
      if (!includeReferences) {
        lastIndex = regex.lastIndex;
        continue;
      }
      const refNumbers = match[2]
        .split(',')
        .map(num => parseInt(num.trim(), 10))
        .filter(num => Number.isFinite(num));
      if (refNumbers.length) {
        nodes.push(
          <span key={`refs-${match.index}`}>
            {refNumbers.map((num, refIdx) => {
              const ref = references.at(num - 1);
              if (!ref) return null;
              const external = ref.href.startsWith('http');
              return (
                <Superscript key={`ref-${num}`} $hasLeadingSpace={refIdx === 0}>
                  <SuperscriptLink
                    href={ref.href}
                    target={external ? '_blank' : undefined}
                    rel={external ? 'noreferrer' : undefined}
                  >
                    {num}
                  </SuperscriptLink>
                  {refIdx < refNumbers.length - 1 && ','}
                </Superscript>
              );
            })}
          </span>,
        );
      }
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

const Paragraph = styled.p`
  margin-bottom: ${oldTheme.spacing.s};
  line-height: 1.6;
`;

const BulletList = styled.ul`
  margin-bottom: ${oldTheme.spacing.s};
  margin-left: ${oldTheme.spacing.l};
  padding-left: ${oldTheme.spacing.xs};
  list-style-type: disc;
`;

const BulletItem = styled.li`
  margin-bottom: 4px;
`;

const Superscript = styled.sup<{ $hasLeadingSpace?: boolean }>`
  margin-left: ${props => (props.$hasLeadingSpace ? '2px' : '0')};
  font-weight: 600;
`;

const InlineSuperscript = styled.sup`
  font-weight: 600;
  margin-left: 1px;
`;

const SuperscriptLink = styled(ChakraLink)`
  color: ${theme.colors.neutral900};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
    color: ${theme.colors.neutral500};
  }
`;
